/**
 * Finance AI Copilot — chat interface grounded in the workspace's live ledger.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const SUGGESTIONS = [
  "What's my current cash position?",
  "Which expense category grew fastest this month?",
  "How much AR is outstanding beyond 60 days?",
  "What's my gross margin this quarter?",
  "Am I profitable this month? Explain the drivers.",
];

export default function FinanceCopilot() {
  const { activeWorkspaceId } = useAuth();
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/finance/workspaces/${activeWorkspaceId}/copilot/history?limit=20`)
      .then((r) => {
        const msgs = (r.data.messages || []).reverse();
        setMessages(msgs.flatMap((m) => [
          { role: "user", text: m.question, id: `q-${m.id}` },
          { role: "assistant", text: m.answer, id: `a-${m.id}` },
        ]));
        if (msgs.length > 0) setSessionId(msgs[msgs.length - 1].session_id);
      });
  }, [activeWorkspaceId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setBusy(true);
    const userMsgId = `q-${Date.now()}`;
    setMessages((prev) => [...prev, { role: "user", text, id: userMsgId }]);
    setQ("");
    try {
      const { data } = await api.post(`/finance/workspaces/${activeWorkspaceId}/copilot/query`, { question: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", text: data.answer, id: `a-${data.message_id}` }]);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Copilot failed"));
      setMessages((prev) => [...prev, { role: "assistant", text: "Sorry, I couldn't respond. Please try again.", id: `err-${Date.now()}`, error: true }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="finance-copilot-page">
      <PageHeader title="Finance Copilot" subtitle="Ask the AI questions grounded in your live ledger. Powered by Claude Sonnet." icon={Bot} />
      <Card className="border-primary/30">
        <CardContent className="p-0 flex flex-col h-[70vh]">
          <div className="flex-1 overflow-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="text-center space-y-4 py-12">
                <Sparkles className="w-10 h-10 mx-auto text-primary" />
                <p className="text-muted-foreground max-w-md mx-auto">Ask me anything about your finances &mdash; I&apos;ll pull the numbers straight from your ledger and give you the story behind them.</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                  {SUGGESTIONS.map((s, i) => (
                    <Button key={i} variant="outline" size="sm" onClick={() => ask(s)} data-testid={`copilot-suggestion-${i}`}>{s}</Button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`copilot-msg-${m.role}`}>
                <div className={`max-w-[75%] rounded-lg px-4 py-3 whitespace-pre-wrap text-sm ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : m.error ? "bg-destructive/10 border border-destructive/30" : "bg-muted/50 border"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start"><div className="max-w-[75%] rounded-lg px-4 py-3 bg-muted/50 border text-sm text-muted-foreground italic" data-testid="copilot-thinking">Analyzing your ledger…</div></div>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t p-4">
            <div className="flex gap-2">
              <Textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                placeholder="Ask about revenue, expenses, cash flow, aging, margins…"
                rows={2}
                className="flex-1"
                data-testid="copilot-input"
              />
              <Button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="copilot-send"><Send className="w-4 h-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Enter to send · Shift+Enter for newline. Answers ground on your live Trial Balance + 90-day P&L + AR aging.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
